"use client"

import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import {
  TaskDescriptionField,
  TaskDueDateField,
  TaskPriorityField,
  TaskTitleField,
  type TaskPriority,
} from "@/components/tasks/task-fields"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
//import { Switch } from "@/components/ui/switch"
import { format } from "date-fns"
import { Briefcase, User } from "lucide-react"
import { useGetCandidatesSearch, useGetJobs } from "openapi/api/endpoints"
import { PostTasksBody } from "openapi/api/model"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
interface NewTaskDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Awaited: the composer stays open, with its form, until this resolves. */
  onCreateTask: (task: PostTasksBody) => void | Promise<void>
  /**
   * The job a task is already about, when the composer is opened from inside
   * that job. Asking someone to pick the job whose page they are standing on is
   * a question with one possible answer, so the picker is replaced by the
   * answer.
   */
  jobId?: string
  /** That job's name. The picker's options come from `/jobs`, which a job page
   *  has no reason to have loaded — a disabled picker with nothing to show
   *  reads as a broken one. */
  jobLabel?: string
}



export function NewTaskDialog({ open, onOpenChange, onCreateTask, jobId, jobLabel }: NewTaskDialogProps) {
  const [dueDate, setDueDate] = useState<Date>()
  const [isSaving, setIsSaving] = useState(false)
  const [isLinked, _] = useState(true)
  const [selectedJob, setSelectedJob] = useState<string>(jobId ?? "")
  const [selectedCandidate, setSelectedCandidate] = useState<string>("")
  /*
    Mounted-but-closed is the normal state of this dialog: the job page keeps
    one around for the console's alert. Fetching both picker lists on every job
    page, for a composer nobody opened — and the jobs list even when the job is
    already settled — is two requests for nothing.
  */
  const {data:jobs}=useGetJobs(undefined,{query:{enabled: open && !jobId}})
  const [dueDateOpen, setDueDateOpen] = useState(false)

  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      title: "",
      description: "",
      priority: "medium",
      linkType: "job",
    },
  })

  const linkType = watch("linkType")

  // Below `linkType`, which it reads — and which `useForm` above produces.
  const {data:candidates}=useGetCandidatesSearch(
    {job_id:selectedJob},
    {query:{enabled: open && linkType === "candidate" && !!selectedJob}},
  )

  // `handleClose` empties the picker, and the caller may open the dialog again
  // on the same job — so the job it was given is re-applied each time it opens.
  useEffect(() => {
    if (open && jobId) setSelectedJob(jobId)
  }, [open, jobId])

  const onSubmit = async (data: any) => {
    if (data.linkType === "job" && !selectedJob) {
      toast.error("Please select a job before creating a linked task.")
      return
    }
    if (data.linkType === "candidate" && !selectedCandidate) {
      toast.error("Please select a candidate before creating a linked task.")
      return
    }
    const dueDateValue = dueDate ? format(dueDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")
    const newTask: PostTasksBody = linkType === "candidate"
      ? {
          title: data.title,
          description: data.description ?? "",
          priority: data.priority,
          due_date: dueDateValue,
          entity_type: "job_candidate",
          candidate_id: selectedCandidate,
          job_id: selectedJob,
        }
      : {
          title: data.title,
          description: data.description ?? "",
          priority: data.priority,
          due_date: dueDateValue,
          entity_type: "jobs",
          entity_id: selectedJob,
        }

    if (newTask.due_date) {
      /*
        Local on both sides.

        The due date above is `format(new Date(), "yyyy-MM-dd")` — the local
        calendar day — and this compared it against `toISOString()`, which is
        UTC. Every evening west of Greenwich the two disagree by a day, so the
        guard rejected its own default and no task could be created at all
        between about 20:00 and midnight, without the user ever picking a date.
      */
      const today = format(new Date(), "yyyy-MM-dd")
      if (newTask.due_date < today) {
        toast.error("Cannot set due date to past date")
        return
      }
    }

    /*
      Await it, and close only if it worked. This fired the callback and reset
      every field in the same breath, so a slow or rejected `POST /tasks` threw
      away a completed form — the toast said what went wrong to someone who no
      longer had the thing that went wrong. Same bug the job wizard had.
    */
    if (isSaving) return
    setIsSaving(true)
    try {
      await onCreateTask(newTask)
    } catch {
      // The caller has already said what went wrong; keep the form intact.
      setIsSaving(false)
      return
    }
    setIsSaving(false)
    handleClose()
  }

  const handleClose = () => {
    reset()
    setDueDate(undefined)
    //setIsLinked(false)
    setSelectedJob("")
    setSelectedCandidate("")
    onOpenChange(false)
  }



  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title="Create New Task"
      description="Something the desk owes, with a date it is owed by."
      footer={
        <>
          <QuietAction size="md" onClick={handleClose}>
            Cancel
          </QuietAction>
          <PageAction tone="primary" type="submit" form="new-task-form">
            Create Task
          </PageAction>
        </>
      }
    >
      <form id="new-task-form" onSubmit={handleSubmit(onSubmit)}>
        <TaskTitleField field={register("title", { required: true })} autoFocus />
        <TaskDescriptionField field={register("description")} />

        {/* Priority and due date share a line once there is room: they are the
            two short answers, and stacking them wastes the dialog's height. */}
        <div className="flex flex-col gap-0 @sm:flex-row @sm:gap-6">
          <TaskPriorityField
            value={(watch("priority") as TaskPriority) ?? "medium"}
            onChange={(value) => setValue("priority", value)}
          />
          <TaskDueDateField
            value={dueDate}
            onChange={setDueDate}
            open={dueDateOpen}
            onOpenChange={setDueDateOpen}
          />
        </div>

        {isLinked && (
          <div className="mt-6 border-t pt-5">
            <FieldShell label="Link type">
              <Select
                onValueChange={(value) => setValue("linkType", value)}
                defaultValue="job"
              >
                <SelectTrigger
                  aria-label="Link type"
                  className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="job">
                    <span className="flex items-center gap-2">
                      <Briefcase className="size-3.5" />
                      Job
                    </span>
                  </SelectItem>
                  <SelectItem value="candidate">
                    <span className="flex items-center gap-2">
                      <User className="size-3.5" />
                      Candidate
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </FieldShell>

            <FieldShell label={jobId ? "Job" : "Select job"} className="mt-5">
              {jobId ? (
                // Opened from a job: state it, do not ask it.
                <p className="border-b pb-[7px] text-[12.5px] leading-[1.4]">
                  {jobLabel ?? "This job"}
                </p>
              ) : (
                <Select onValueChange={setSelectedJob} value={selectedJob}>
                  <SelectTrigger
                  aria-label="Job"
                    className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
                  >
                    <SelectValue placeholder="Choose a job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs?.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} - {job.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FieldShell>

            {linkType === "candidate" && selectedJob && (
              <FieldShell label="Select candidate" className="mt-5">
                <Select onValueChange={setSelectedCandidate} value={selectedCandidate}>
                  <SelectTrigger
                  aria-label="Candidate"
                    className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
                  >
                    <SelectValue placeholder="Choose a candidate..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/*
                      `candidate.id` is the *job-candidate link*, per the
                      contract; the candidate is `candidate.candidates.id`,
                      which is what `POST /tasks` wants. Sending the link id
                      hangs the task off a UUID that names something else.
                    */}
                    {candidates?.map((candidate) => (
                      <SelectItem
                        key={candidate.id}
                        value={candidate.candidates?.id ?? ""}
                      >
                        {candidate.candidates?.full_name} ({candidate.stage})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldShell>
            )}
          </div>
        )}
      </form>
    </DialogShell>
  )
}
